// Thin fetch wrapper.
//
// The maintenance interface and the proxy Worker share the hostname
// hmy-ratecard-admin.netevo.com.au in production. Cloudflare Access
// stamps every request with Cf-Access-Authenticated-User-Email; the
// proxy Worker trusts that header for authorisation.
//
// In local development the Vite dev server proxies /api/* to a locally
// running proxy Worker (wrangler dev). The proxy Worker accepts an
// X-Dev-Identity header in place of the Cloudflare Access header when
// env.DEV_MODE === 'true'. This module attaches X-Dev-Identity from the
// dev-identity store when the interface is running under Vite's dev
// server. In production builds the header is a no-op (the proxy ignores
// it because env.DEV_MODE is unset).

import type {
  CatalogueResponse,
  ConflictResponse,
  DownstreamConflictResponse,
  DownstreamQueueAcknowledgement,
  FourPartTimestamp,
  IdentityResult,
  ListDraftsResponse,
  LoadDraftResponse,
  ManifestResponse,
  ProposalDetail,
  ProposalSummary,
  ProposalTimelineResponse,
  ProposeResponse,
  SaveDraftResponse,
  StagedChangesPayload,
  StaleQueuedResponse,
  ValidateResponse
} from './types';

const IS_DEV = import.meta.env.DEV;

// The catalogue and manifest endpoints live on the read-path Worker at
// hmy-ratecard.netevo.com.au (not on the proxy Worker that serves the
// /api/* routes for state-changing operations). In dev the Vite proxy
// rewrites /api-catalogue/* to that hostname; in production the
// interface calls the absolute URL directly. The read-path Worker
// returns Access-Control-Allow-Origin: * so cross-origin works without
// preflight for simple GETs, and the catalogue surface is public so we
// do not need to forward credentials.
const CATALOGUE_BASE = IS_DEV ? '/api-catalogue' : 'https://hmy-ratecard.netevo.com.au/api';

let devIdentity: string | null = null;

export function setDevIdentity(email: string | null): void {
  devIdentity = email;
  if (typeof window !== 'undefined' && email) {
    window.sessionStorage.setItem('dev-identity', email);
  } else if (typeof window !== 'undefined') {
    window.sessionStorage.removeItem('dev-identity');
  }
}

export function getDevIdentity(): string | null {
  if (devIdentity) return devIdentity;
  if (typeof window === 'undefined') return null;
  return window.sessionStorage.getItem('dev-identity');
}

export class ApiError extends Error {
  status: number;
  code: string;
  detail: unknown;
  constructor(status: number, code: string, message: string, detail: unknown = null) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.code = code;
    this.detail = detail;
  }
}

interface RequestOptions extends RequestInit {
  // The proxy Worker routes need the Access cookie + X-Dev-Identity
  // header in dev. The cross-origin read-path Worker does not, and
  // would reject credentialed requests against its Allow-Origin: *
  // response. crossOrigin: true skips credential forwarding and the
  // dev-identity header.
  crossOrigin?: boolean;
}

async function request<T>(
  path: string,
  init: RequestOptions = {}
): Promise<T> {
  const headers = new Headers(init.headers);
  headers.set('Accept', 'application/json');
  if (IS_DEV && !init.crossOrigin) {
    const id = getDevIdentity();
    if (id) headers.set('X-Dev-Identity', id);
  }
  if (init.body !== undefined && !headers.has('Content-Type')) {
    headers.set('Content-Type', 'application/json');
  }

  const credentials: RequestCredentials = init.crossOrigin ? 'omit' : 'include';
  const response = await fetch(path, { ...init, headers, credentials });
  const contentType = response.headers.get('Content-Type') ?? '';
  const isJson = contentType.includes('application/json');
  const body = isJson ? await response.json().catch(() => null) : await response.text();

  if (!response.ok) {
    const errorBody = (isJson ? body : null) as
      | { error?: { code?: string; message?: string; detail?: unknown } }
      | null;
    const code = errorBody?.error?.code ?? 'http_error';
    const message = errorBody?.error?.message ?? `Request failed (${response.status})`;
    throw new ApiError(response.status, code, message, errorBody?.error?.detail ?? body);
  }

  return body as T;
}

export const api = {
  identity(): Promise<IdentityResult> {
    return request<IdentityResult>('/api/identity');
  },

  accessControl(): Promise<{
    schema_version: string | null;
    users: Array<{
      email: string;
      display_name: string | null;
      github_username: string | null;
      roles: string[];
      added_at: string | null;
    }>;
  }> {
    return request('/api/access-control');
  },

  listDrafts(): Promise<ListDraftsResponse> {
    return request<ListDraftsResponse>('/api/drafts');
  },

  loadDraft(draftSlug: string): Promise<LoadDraftResponse> {
    return request<LoadDraftResponse>(`/api/drafts/${encodeURIComponent(draftSlug)}`);
  },

  saveDraft(draftSlug: string, payload: StagedChangesPayload): Promise<SaveDraftResponse> {
    return request<SaveDraftResponse>(`/api/drafts/${encodeURIComponent(draftSlug)}`, {
      method: 'POST',
      body: JSON.stringify(payload)
    });
  },

  discardDraft(draftSlug: string): Promise<{ archived_at: string; archive_branch: string; discarded_branch: string }> {
    return request(`/api/drafts/${encodeURIComponent(draftSlug)}`, { method: 'DELETE' });
  },

  // Begins a new draft pre-filled with an archived entry's staged
  // changes (INTERFACE-SPECIFICATION.md section 8.3). The Archive is
  // read-only history; this is the only forward action on an archived
  // entry. The new draft is rooted at current main, so deltas may
  // render differently from the archived entry if cells touched by
  // the archived changes have moved on main since closure.
  newDraftFromArchive(archivedDraftSlug: string, newDraftSlug: string): Promise<{
    branch: string;
    commit_sha: string;
    saved_at: string;
    deltas: unknown[];
    value_type: string;
    seeded_from_archive: string;
    seeded_edit_count: number;
  }> {
    return request(`/api/drafts/${encodeURIComponent(archivedDraftSlug)}/new-from-archive`, {
      method: 'POST',
      body: JSON.stringify({ new_draft_slug: newDraftSlug })
    });
  },

  // Schedule path: caller supplies effective_from_local + timezone.
  // Release-now path: caller supplies release_now: true; the server
  // sets effective_from to the submission instant in the proposer's
  // jurisdiction timezone. Either path is valid; at least one must
  // be present.
  promoteDraft(draftSlug: string, payload: {
    substantiation_url: string;
    effective_from_local?: string;
    effective_from_timezone?: string;
    // Set when the picker had to disambiguate a DST fall-back local
    // time. The server (catalogue-client.proposeChange) forwards the
    // value to tz-utils.localToUtc, which resolves to the chosen
    // occurrence instead of throwing AmbiguousTimeError.
    effective_from_occurrence?: 'first' | 'second';
    release_now?: boolean;
    reviewer: string;
    change_description: string;
    acknowledged_warnings?: string[];
    // INTERFACE-SPECIFICATION.md §7.4. Sent when the proposer has
    // acknowledged that one or more queued manifest entries would
    // silently overwrite cells edited by this change. The server
    // stamps it on _governance.last_proposal so the audit record
    // captures the warning was shown and acknowledged.
    downstream_queue_acknowledged?: DownstreamQueueAcknowledgement;
  }): Promise<ProposeResponse> {
    return request<ProposeResponse>(`/api/drafts/${encodeURIComponent(draftSlug)}/promote`, {
      method: 'POST',
      body: JSON.stringify(payload)
    });
  },

  draftConflict(draftSlug: string): Promise<ConflictResponse> {
    return request<ConflictResponse>(`/api/drafts/${encodeURIComponent(draftSlug)}/conflict`);
  },

  validate(payload: StagedChangesPayload & { reviewer?: string }): Promise<ValidateResponse> {
    return request<ValidateResponse>('/api/validate', {
      method: 'POST',
      body: JSON.stringify(payload)
    });
  },

  // INTERFACE-SPECIFICATION.md §7.4 downstream-queue conflict check.
  // Returns any approved manifest entries with later effective_from
  // for the same (jurisdiction, product) whose frozen snapshots would
  // silently overwrite cells edited by the new change. The SPA passes
  // the proposed effective_from UTC instant alongside the staged
  // edits so the server can compare cell-by-cell against each queued
  // snapshot in KV. An empty `conflicts` array means submission can
  // proceed without acknowledgement; a non-empty one means the panel
  // and acknowledgement checkbox are required.
  detectDownstreamConflict(payload: {
    jurisdiction: string;
    product: string;
    proposed_effective_from_utc: string;
    cube_edits?: Array<{ coordinates: string; new_value: Record<string, unknown> }>;
    fee_edits?: Array<{ path: string; new_value: unknown }>;
    fee_override_edits?: Array<{ credit_rating_id: string; new_value: unknown }>;
    purpose_edits?: Array<{ action: 'add' | 'remove' | 'update'; purpose_id?: string; purpose?: unknown }>;
    amount_edits?: Array<{ field: string; new_value: unknown }>;
    term_edits?: number[];
  }): Promise<DownstreamConflictResponse> {
    return request<DownstreamConflictResponse>('/api/validate/downstream-conflict', {
      method: 'POST',
      body: JSON.stringify(payload)
    });
  },

  // Same Schedule / Release-now contract as promoteDraft. proposeChange
  // is the one-shot path used by system-driven integrations
  // (Camunda, MCP agents); promoteDraft is the human-driven path the
  // maintenance interface uses.
  proposeChange(payload: StagedChangesPayload & {
    substantiation_url: string;
    effective_from_local?: string;
    effective_from_timezone?: string;
    effective_from_occurrence?: 'first' | 'second';
    release_now?: boolean;
    reviewer: string;
    change_description: string;
    acknowledged_warnings?: string[];
    downstream_queue_acknowledged?: DownstreamQueueAcknowledgement;
  }): Promise<ProposeResponse> {
    return request<ProposeResponse>('/api/proposals', {
      method: 'POST',
      body: JSON.stringify(payload)
    });
  },

  listProposals(status: 'open' | 'merged' | 'all' | 'changes_requested' | 'closed' | 'completed' = 'open'): Promise<ProposalSummary[]> {
    return request<ProposalSummary[]>(`/api/proposals?status=${status}`);
  },

  loadProposal(prNumber: number): Promise<ProposalDetail> {
    return request<ProposalDetail>(`/api/proposals/${prNumber}`);
  },

  loadProposalTimeline(prNumber: number): Promise<ProposalTimelineResponse> {
    return request<ProposalTimelineResponse>(`/api/proposals/${prNumber}/timeline`);
  },

  approveProposal(prNumber: number, comment?: string): Promise<{ pr_number: number; merged: boolean; merge_commit_sha: string }> {
    return request(`/api/proposals/${prNumber}/approve`, {
      method: 'POST',
      body: JSON.stringify(comment ? { comment } : {})
    });
  },

  requestChanges(prNumber: number, comment: string): Promise<{ pr_number: number; review_id: number }> {
    return request(`/api/proposals/${prNumber}/request-changes`, {
      method: 'POST',
      body: JSON.stringify({ comment })
    });
  },

  // Resubmit a proposal after a REQUEST_CHANGES review. The payload's
  // shape mirrors the propose / promote-draft contracts: callers pass
  // whichever edit categories the in-flight proposal touches. The
  // server-side resubmitProposal in tools/catalogue-client.mjs enforces
  // strict value-type alignment with the original proposal, so callers
  // do not need to omit non-matching categories — the server rejects
  // mismatches with a ValidationError.
  resubmitProposal(prNumber: number, edits: {
    cube_edits?: Array<{ coordinates: string; new_value: { interest_rate_pa?: number; max_loan_amount?: number } }>;
    fee_edits?: Array<{ path: string; new_value: unknown }>;
    fee_override_edits?: Array<{ credit_rating_id: string; new_value: unknown }>;
    purpose_edits?: Array<{ action: 'add' | 'remove' | 'update'; purpose_id?: string; purpose?: unknown }>;
    amount_edits?: Array<{ field: string; new_value: unknown }>;
    term_edits?: number[];
  }, revision_description?: string): Promise<{
    pr_number: number;
    branch: string;
    new_commit_sha: string;
    revised_at: string;
    revised_by: string;
  }> {
    return request(`/api/proposals/${prNumber}/revise`, {
      method: 'POST',
      body: JSON.stringify({ ...edits, revision_description })
    });
  },

  // Closes the proposed PR without merging. Used by the Pending
  // Review card and the Revise interface (INTERFACE-SPECIFICATION.md
  // sections 8.1 + 8.4). Server-side authorisation: original proposer
  // or admin only.
  discardProposal(prNumber: number, reason?: string): Promise<{
    pr_number: number;
    branch: string;
    closed_at: string;
    discarded_by: string;
    reason: string | null;
  }> {
    return request(`/api/proposals/${prNumber}/discard`, {
      method: 'POST',
      body: JSON.stringify(reason ? { reason } : {})
    });
  },

  // PR 3.2.3 reviewer-driven queue actions on Approved & Queued
  // manifest entries. publishNow fast-forwards a queued entry to
  // in_effect immediately (the merge already happened upstream; this
  // is a separate governance action). dequeue cancels an
  // approved-but-pre-live entry. Both require a justification string
  // that the audit record retains; the server enforces
  // reviewer:{value_type} or admin.
  publishNowManifestEntry(catalogueVersion: string, justification: string): Promise<{
    catalogue_version: string;
    git_hash: string;
    status: 'in_effect';
    effective_from: FourPartTimestamp;
    proposed_effective_from?: FourPartTimestamp;
    published_now_at: string;
    published_now_by: string;
    published_now_reason: string;
    transitions: Array<{ kind: string; jurisdiction: string; product: string; version?: string; from?: string; to?: string }>;
  }> {
    return request(`/api/manifest-entries/${encodeURIComponent(catalogueVersion)}/publish-now`, {
      method: 'POST',
      body: JSON.stringify({ justification })
    });
  },

  dequeueManifestEntry(catalogueVersion: string, justification: string): Promise<{
    catalogue_version: string;
    git_hash: string;
    status: 'dequeued';
    dequeued_at: string;
    dequeued_by: string;
    dequeued_reason: string;
  }> {
    return request(`/api/manifest-entries/${encodeURIComponent(catalogueVersion)}/dequeue`, {
      method: 'POST',
      body: JSON.stringify({ justification })
    });
  },

  // INTERFACE-SPECIFICATION.md §8.1 staleness check. Returns the
  // intermediate manifest entries that have gone live on the same
  // (jurisdiction, product) between this queued entry's approved_at
  // and its effective_from.utc, plus the cell-level diff each one
  // will be overwritten on when the queued entry activates. Empty
  // `intermediate` array means the queued entry is fresh and the
  // staleness banner is not rendered.
  staleQueuedEntry(catalogueVersion: string): Promise<StaleQueuedResponse> {
    return request<StaleQueuedResponse>(
      `/api/manifest-entries/${encodeURIComponent(catalogueVersion)}/staleness`
    );
  },

  // INTERFACE-SPECIFICATION.md §8.1 re-author affordance. Creates a
  // new draft on the calling user's branch namespace rooted at
  // current main, pre-filled with the cube edits that reconstruct
  // the queued entry's intent (diffed against the predecessor entry
  // whose snapshot will be live just before the queued entry fires).
  // The draft cube carries _governance.reauthor_of_queued so the
  // approval-time auto-dequeue chain finds the original queued
  // entry and de-queues it after the new proposal merges.
  reauthorQueuedEntry(catalogueVersion: string): Promise<{
    branch: string;
    commit_sha: string;
    saved_at: string;
    draft_slug: string;
    jurisdiction: 'au' | 'nz';
    product: 'unsecured-personal-loan' | 'secured-vehicle-loan';
    reauthored_from: string;
    predecessor_catalogue_version: string;
    seeded_edit_count: number;
    value_type: string | null;
  }> {
    return request(
      `/api/manifest-entries/${encodeURIComponent(catalogueVersion)}/re-author`,
      { method: 'POST', body: JSON.stringify({}) }
    );
  },

  // Read-path Worker reads — public catalogue and manifest. The
  // read-path Worker lives on a different hostname
  // (hmy-ratecard.netevo.com.au) and serves Access-Control-Allow-Origin:
  // * so simple cross-origin GETs work without preflight. crossOrigin
  // skips credential forwarding so the read-path Worker's wildcard CORS
  // header is honoured by the browser.

  catalogue(jurisdiction: string, product: string, hash?: string): Promise<CatalogueResponse> {
    const suffix = hash ? `?hash=${encodeURIComponent(hash)}` : '';
    return request<CatalogueResponse>(`${CATALOGUE_BASE}/catalogue/${jurisdiction}/${product}${suffix}`, { crossOrigin: !IS_DEV });
  },

  manifest(): Promise<ManifestResponse> {
    return request<ManifestResponse>(`${CATALOGUE_BASE}/manifest`, { crossOrigin: !IS_DEV });
  }
};
